type RawValue = string | number | boolean | null | undefined;

export type NormalizedAuthResponse = {
  accessToken?: string;
  refreshToken?: string;
  user: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
    phoneNumberVerified?: boolean | null;
  };
  message?: string;
};

const coerceString = (value: RawValue): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  return undefined;
};

const coerceBoolean = (value: RawValue): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "n"].includes(normalized)) {
      return false;
    }
  }

  return undefined;
};

const extractFromSources = (
  sources: Array<Record<string, unknown> | undefined>,
  keys: string[]
): string | undefined => {
  for (const source of sources) {
    if (!source) continue;

    for (const key of keys) {
      const candidate = (source as Record<string, unknown>)[key] as RawValue;
      const value = coerceString(candidate);
      if (value) {
        return value;
      }
    }
  }

  return undefined;
};

const extractBooleanFromSources = (
  sources: Array<Record<string, unknown> | undefined>,
  keys: string[]
): boolean | undefined => {
  for (const source of sources) {
    if (!source) continue;

    for (const key of keys) {
      const candidate = (source as Record<string, unknown>)[key] as RawValue;
      const value = coerceBoolean(candidate);
      if (value !== undefined) {
        return value;
      }
    }
  }

  return undefined;
};

export const normalizeAuthResponse = (
  rawResponse: unknown,
  fallbackEmail?: string,
  options?: { requireToken?: boolean }
): NormalizedAuthResponse => {
  const raw = (rawResponse ?? {}) as Record<string, unknown>;
  const nested = (raw.data ?? {}) as Record<string, unknown>;
  const deeper = (nested.data ?? {}) as Record<string, unknown>;
  const sources = [raw, nested, deeper];

  const tokenContainers = [
    raw.tokens as Record<string, unknown> | undefined,
    nested.tokens as Record<string, unknown> | undefined,
    deeper.tokens as Record<string, unknown> | undefined,
  ];

  const accessToken =
    extractFromSources(sources, ["accessToken", "access_token", "token"]) ??
    extractFromSources(tokenContainers, [
      "accessToken",
      "access_token",
      "token",
    ]);

  if (!accessToken && options?.requireToken !== false) {
    throw new Error("Missing access token in auth response");
  }

  const refreshToken =
    extractFromSources(sources, ["refreshToken", "refresh_token"]) ??
    extractFromSources(tokenContainers, ["refreshToken", "refresh_token"]);

  const userCandidates = [
    raw.user as Record<string, unknown> | undefined,
    nested.user as Record<string, unknown> | undefined,
    deeper.user as Record<string, unknown> | undefined,
    raw.userData as Record<string, unknown> | undefined,
    nested.userData as Record<string, unknown> | undefined,
    deeper.userData as Record<string, unknown> | undefined,
  ];

  const userCandidate = userCandidates.find(
    (candidate) => candidate && Object.keys(candidate).length > 0
  );

  const userId =
    extractFromSources(
      [userCandidate],
      ["id", "userId", "user_id", "_id", "uid"]
    ) ??
    fallbackEmail ??
    "unknown";
  const email =
    extractFromSources(
      [userCandidate],
      ["email", "userEmail", "username", "contact_email", "useremail"]
    ) ??
    fallbackEmail ??
    "unknown";
  const firstName =
    extractFromSources(
      [userCandidate],
      ["firstName", "first_name", "firstname", "givenName"]
    ) ?? null;
  const lastName =
    extractFromSources(
      [userCandidate],
      ["lastName", "last_name", "lastname", "familyName"]
    ) ?? null;
  const phoneNumber =
    extractFromSources(
      [userCandidate],
      [
        "phoneNumber",
        "phone",
        "phone_number",
        "contactNumber",
        "mobile",
        "mobileNumber",
        "whatsapp",
      ]
    ) ?? null;
  const phoneNumberVerified =
    extractBooleanFromSources(
      [userCandidate],
      [
        "phoneNumberVerified",
        "phone_verified",
        "isPhoneVerified",
        "phoneNumberValidated",
        "isPhoneLinked",
        "phoneLinked",
      ]
    ) ?? null;

  const message = extractFromSources(sources, [
    "message",
    "detail",
    "status",
    "description",
  ]);

  return {
    accessToken,
    refreshToken,
    user: {
      id: userId,
      email,
      firstName,
      lastName,
      phoneNumber,
      phoneNumberVerified,
    },
    message: message ?? undefined,
  };
};
